<!--
  Carousel card media override picker.

  This must be a Vivid `VDialog` with `modal`, NOT an `ion-modal`. One of its hosts
  is `whats-app-create-message.vue`, which Live Chat renders inside the
  "Send template message" `<VDialog … modal>` (src/pages/SingleCustomerChats.vue).
  A Vivid modal dialog calls `showModal()` on a native `<dialog>`, which promotes it
  into the browser's *native top layer*. The top layer paints above every
  normal-flow element regardless of `z-index`, and `showModal()` also marks
  everything that is not a descendant of the dialog `inert`.

  An `ion-modal` is ordinary normal-flow content with a high `z-index`, so it was
  painted *behind* the template editing dialog and was inert on top of that —
  the media picker was unreachable. A nested modal `<dialog>` instead enters the
  top layer *after* the parent (paint order is entry order) and stays a DOM
  descendant of it, so it paints above and remains interactive.
-->
<template>
  <VDialog
    v-if="isOpen"
    :open="isOpen"
    :headline="cardIndex >= 0 ? `Card ${cardIndex + 1} - Media Override` : 'Carousel Card Media'"
    modal
    @close="closeModal"
    :style="{ '--dialog-min-inline-size': '500px', '--dialog-max-inline-size': '600px' }"
  >
    <template #body>
      <div class="carousel-header-upload-wrapper">
        <div v-if="cardData" class="carousel-header-current-media">
          <p class="sub-title ion-no-margin paddingBottom">Current media for Card {{ cardIndex + 1 }}</p>
          <div v-if="cardData.components[0].image_url && !cardData.components[0].overridden_url" class="current-media-preview">
            <img v-if="cardData.components[0].format === 'IMAGE'" :src="sanitizeHardLinkUrl(cardData.components[0].image_url)" class="carousel-header-preview-img" alt="current card media" />
            <video v-else :src="sanitizeHardLinkUrl(cardData.components[0].image_url)" controls preload="metadata" class="carousel-header-preview-img"></video>
          </div>
          <div v-if="cardData.components[0].overridden_url" class="current-media-preview">
            <div class="overridden-media-info">
              <VIcon :name="cardData.components[0].format === 'IMAGE' ? imageOutline : videocamOutline" class="defaultColor"></VIcon>
              <p class="title ion-no-margin">{{ cardData.components[0].overridden_url }}</p>
              <button @click="removeOverride" class="icon-btn" title="Reset to template default">
                <VIcon name="delete-line" class="trashIcon"></VIcon>
              </button>
            </div>
          </div>
        </div>
        <div class="carousel-header-upload-section">
          <p class="sub-title paddingBottom">Upload new media or paste URL to override</p>
          <upload-files-v-2
            uploadURLEnabled
            allowDynamicURL
            :allowedImageTypes="cardFormat === 'IMAGE' ? 'image/png, image/jpeg, image/jpg' : undefined"
            :allowedVideoTypes="cardFormat === 'VIDEO' ? 'video/mp4' : undefined"
            :type="cardFormat === 'IMAGE' ? 'image' : 'video'"
            :multiple="false"
            :id="'carouselHeaderUpload_' + cardIndex"
            :maxFileSizeAllowed="cardFormat === 'IMAGE' ? 5 : 16"
            :media_url="mediaUrl"
            :uploaded_url_type="urlType"
            :meta="{is_export: false, is_broadcast: false, is_redact: false, is_public: true, conversation_id: undefined, channel: 'whatsapp'}"
            v-model:loading="filesUploading"
            @send-upload-data="handleFileUpload"
            @url-uploaded="(uploadedUrlData) => handleUrlUpload(uploadedUrlData.file_url)"
            @url-type-changed="(value) => { urlType = value; }"
          >
            <p class="sub-title">{{ cardFormat === 'IMAGE' ? 'Image' : 'Video' }}</p>
            <div class="flexContainer">
              <div class="info-box">
                <VIcon name="info-line" :size="-5" connotation="cta" class="infoIcon"></VIcon>
                <p class="sub-title ion-no-margin">{{ cardFormat === 'IMAGE' ? 'Max file size 5MB (.jpeg, .jpg, .png only)' : 'Max file size 16MB (.mp4 only)' }}</p>
              </div>
              <div class="flexBox">
                <button :disabled="filesUploading" class="btn-outline-secondary">
                  <VIcon v-if="!filesUploading" :name="cardFormat === 'IMAGE' ? imageOutline : videocamOutline" class="icon"></VIcon>
                  <VProgressRing v-if="filesUploading" :size="-5" />
                  <span v-else>{{ cardFormat === 'IMAGE' ? 'Upload Image' : 'Upload Video' }}</span>
                </button>
              </div>
            </div>
          </upload-files-v-2>
        </div>
      </div>
    </template>
  </VDialog>
</template>

<script>
import uploadFilesV2 from "@/components/uploadFilesV2.vue";
import { sanitizeHardLinkUrl } from '@/helpers/message.helpers';
import { useUIStore } from '@/stores/ui';

const imageOutline = 'image-line';
const videocamOutline = 'video-line';

export default {
  components: { uploadFilesV2 },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    cardIndex: {
      type: Number,
      default: -1
    },
    cardData: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'media-updated', 'media-removed'],
  data() {
    return {
      imageOutline,
      videocamOutline,
      filesUploading: false,
      urlType: 'static',
      mediaUrl: ''
    };
  },
  computed: {
    cardFormat() {
      return this.cardData?.components?.[0]?.format || 'IMAGE';
    }
  },
  watch: {
    isOpen(newVal) {
      if (newVal && this.cardData) {
        // Pre-fill if override already exists
        if (this.cardData.components[0]?.overridden_url) {
          this.mediaUrl = this.cardData.components[0].overridden_url;
          this.urlType = this.cardData.components[0].overridden_url_type || 'static';
        } else {
          this.mediaUrl = '';
          this.urlType = 'static';
        }
      }
    }
  },
  methods: {
    sanitizeHardLinkUrl,
    closeModal() {
      this.filesUploading = false;
      this.mediaUrl = '';
      this.urlType = 'static';
      this.$emit('close');
    },
    handleFileUpload(urlData) {
      try {
        const mediaUrl = urlData[0]?.url || urlData[0]?.image;
        if (mediaUrl) {
          useUIStore().updateToast({
            open: true,
            message: `Card ${this.cardIndex + 1} media updated successfully`,
            type: "success"
          });
          this.$nextTick(() => {
            this.filesUploading = false;
            this.$emit('media-updated', { cardIndex: this.cardIndex, mediaUrl, urlType: 'static' });
            this.closeModal();
          });
        }
      } catch (error) {
        console.error("Exception: ", error);
        this.filesUploading = false;
        useUIStore().updateToast({ open: true, message: "Something went wrong", type: "danger" });
      }
    },
    handleUrlUpload(fileUrl) {
      if (fileUrl) {
        useUIStore().updateToast({
          open: true,
          message: `Card ${this.cardIndex + 1} media updated successfully`,
          type: "success"
        });
        this.$nextTick(() => {
          this.filesUploading = false;
          this.$emit('media-updated', { cardIndex: this.cardIndex, mediaUrl: fileUrl, urlType: this.urlType || 'static' });
          this.closeModal();
        });
      }
    },
    removeOverride() {
      this.$emit('media-removed', { cardIndex: this.cardIndex });
      this.mediaUrl = '';
      useUIStore().updateToast({
        open: true,
        message: `Card ${this.cardIndex + 1} media reset to template default`,
        type: "success"
      });
    }
  }
};
</script>

<style scoped>
.carousel-header-upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.carousel-header-current-media {
  border-bottom: 1px solid var(--background-primary);
  padding-bottom: 15px;
}
.current-media-preview {
  margin-top: 10px;
}
.carousel-header-preview-img {
  max-width: 200px;
  max-height: 150px;
  border-radius: 5px;
  object-fit: cover;
}
.carousel-header-upload-section {
  padding-top: 5px;
}
.overridden-media-info {
  display: flex;
  align-items: center;
  gap: 10px;
  background: var(--background-primary);
  border-radius: 5px;
  padding: 10px;
}
.overridden-media-info .title {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.sub-title {
  font-weight: 400;
  font-size: 14px;
  line-height: 16px;
  color: var(--gray-04);
}
.flexContainer {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.info-box {
  display: flex;
  align-items: center;
  flex-direction: row;
  background: var(--purple-opac);
  padding: 5px 10px;
  border-radius: 5px;
}
.infoIcon {
  color: var(--purple);
  padding-right: 10px;
  width: 25px;
  height: 25px;
}
.icon {
  padding-right: 5px;
}
.flexBox {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.btn-outline-secondary {
  background: transparent;
  border: 1px solid var(--purple);
  color: var(--purple);
  padding: 8px 16px;
  border-radius: 5px;
  cursor: pointer;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
}
.btn-outline-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.icon-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
}
.trashIcon {
  color: var(--ion-color-danger, #d32f2f);
}
</style>
